import type { Ref } from 'vue';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { isString } from '@utils/guards';

import type { IHistoryOfTrackerGroupsChangesItem } from '../../../types';
import { useDeleteHistoryOfTrackerGroupsMutation } from '../../../mutations';
import { useShowHistoryOfTrackerGroupsQuery } from '../../../queries';

export interface ITrackerGroupsHistoryComposable {
  historyOfTrackerGroupsChanges: Ref<IHistoryOfTrackerGroupsChangesItem[]>;
  isHistoryTrackerGroupsLoading: Ref<boolean>;
  onHistoryDelete(entry: IHistoryOfTrackerGroupsChangesItem): Promise<void>;
}

export default function useTrackerGroupsHistory(): ITrackerGroupsHistoryComposable {
  const route = useRoute();

  const affiliateId = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));

  const { data: historyTrackerGroupsData, isLoading: isHistoryTrackerGroupsLoading, setData } = useShowHistoryOfTrackerGroupsQuery(affiliateId);

  const { mutateAsync: removeHistoryTrackerGroups } = useDeleteHistoryOfTrackerGroupsMutation();

  const historyOfTrackerGroupsChanges = computed(() => historyTrackerGroupsData.value?.history_of_tracker_groups_changes ?? []);

  async function onHistoryDelete(entry: IHistoryOfTrackerGroupsChangesItem): Promise<void> {
    try {
      await removeHistoryTrackerGroups(entry.id);
      updateHistoryOfTrackerGroups(historyOfTrackerGroupsChanges.value.filter(({ id }) => id !== entry.id));
    } catch {}
  }

  function updateHistoryOfTrackerGroups(list?: IHistoryOfTrackerGroupsChangesItem[]): void {
    setData((queryData) => {
      if (!queryData) {
        return queryData;
      }

      return {
        ...queryData,
        history_of_tracker_groups_changes: list ?? queryData.history_of_tracker_groups_changes,
      };
    });
  }

  return {
    historyOfTrackerGroupsChanges,
    isHistoryTrackerGroupsLoading,
    onHistoryDelete,
  };
}
